import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import InputBase from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import ProgramModal from "@/ui/modals/programs/ProgramModal";
import { useArchiveProgram, usePrograms } from "@/hooks/usePrograms";
import { useOrganizations } from "@/hooks/useOrganizations";
import { Programs } from "@/domain/programs.interface";
import { useAppTheme } from "@/utils/theme";
import { usePageStore } from "@/hooks/usePageStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import useDebounce from "@/hooks/useDebounce";
import { toTitleCase } from "@/utils/toTitleCase";
import { ErrorResponse } from "@/domain/error.interface";

type Filters = {
  search: string;
  status: string;
  orgUnitId: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

const DEFAULT_FILTERS: Filters = {
  search: "",
  status: "",
  orgUnitId: "",
  pageNumber: 0,
  pageSize: 10,
  sortField: "name",
  sortOrder: 1,
};

type StatusChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "info";

const statusChipColor = (status: string): StatusChipColor => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "PENDING":
      return "warning";
    case "CANCELED":
      return "error";
    case "COMPLETED":
      return "default";
    default:
      return "default";
  }
};

const ProgramsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const debouncedSearch = useDebounce({ value: filters.search, delay: 500 });

  const [modalState, setModalState] = useState<{
    open: boolean;
    program?: Programs | null;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    program?: Programs;
  }>({ open: false });

  const archiveProgram = useArchiveProgram();

  const { programsList, isLoadingProgramsList, isFetchingProgramsList } =
    usePrograms({
      search: debouncedSearch,
      status: filters.status,
      orgUnitId: filters.orgUnitId,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
    });

  const { organizations } = useOrganizations({
    pageNumber: 0,
    pageSize: 500,
    sortField: "name",
    sortOrder: 1,
  });

  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    (organizations?.list || []).forEach((o) => {
      if (o._id) map.set(o._id, o.name || o._id);
    });
    return map;
  }, [organizations]);

  const organizationOptions = useMemo(
    () => organizations?.list || [],
    [organizations]
  );

  useEffect(() => {
    setPageTitle("Programs");
  }, []);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, pageNumber: 0 }));
  }, [debouncedSearch, filters.status, filters.orgUnitId]);

  const handleOpenCreate = () => setModalState({ open: true, program: null });

  const handleOpenEdit = (program: Programs) =>
    setModalState({ open: true, program });

  const handleCloseModal = () => setModalState({ open: false });

  const handleConfirmDelete = async () => {
    const target = deleteAlert.program;
    if (!target) return;
    try {
      await archiveProgram.mutateAsync(target._id);
      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "220px",
    },
    {
      id: "eventName",
      isSortable: false,
      disablePadding: false,
      label: "Event",
      align: "left",
      width: "180px",
    },
    {
      id: "orgUnits",
      isSortable: false,
      disablePadding: false,
      label: "Organizations",
      align: "left",
      width: "260px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "140px",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "160px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!programsList || programsList.list.length === 0) return [];

    return programsList.list.map((program, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(program),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, program }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const orgIds: string[] = Array.isArray(program.orgUnitIds)
        ? program.orgUnitIds
        : Array.isArray(program.orgUnitId)
          ? program.orgUnitId
          : [];
      const orgNames = orgIds
        .map((id) => orgNameById.get(id) || id)
        .filter(Boolean);

      return createDataRow(index, {
        name: <div>{program.name}</div>,
        eventName: (
          <Typography variant="bodyMedium" component="div">
            {program.eventName || "-"}
          </Typography>
        ),
        orgUnits:
          orgNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {orgNames.map((name, i) => (
                <Chip
                  key={`${program._id}-org-${i}`}
                  size="small"
                  label={<div className="text-xs">{name}</div>}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="bodyMedium">-</Typography>
          ),
        status: (
          <Chip
            size="small"
            color={statusChipColor(program.status)}
            label={
              <div className="text-xs">
                {toTitleCase(program.status || "-")}
              </div>
            }
          />
        ),
        createdAt: (
          <div>
            {program.createdAt
              ? dayjs(program.createdAt).format("DD MMM YYYY")
              : "-"}
          </div>
        ),
        action: <ActionMenu id={program._id} options={actionMenu} />,
      });
    });
  }, [programsList, theme, orgNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Programs
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    width: "100%",
                    flexDirection: {
                      xs: "column",
                      sm: "column",
                      md: "row",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.surface.light2,
                      px: 2,
                      width: {
                        xs: "100%",
                        md: "45%",
                      },
                    }}
                    className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
                  >
                    <InputBase
                      className="flex-1"
                      placeholder="Search program"
                      inputProps={{ "aria-label": "search" }}
                      onChange={(e) => {
                        setFilters((f) => ({
                          ...f,
                          search: e.target.value,
                        }));
                      }}
                      value={filters.search}
                    />
                    <SearchIcon className="text-icon-primary" />
                  </Box>

                  <FormControl
                    sx={{
                      width: {
                        xs: "100%",
                        md: "200px",
                      },
                    }}
                    size="small"
                  >
                    <InputLabel id="programs-status-filter-label">
                      Status
                    </InputLabel>
                    <Select
                      labelId="programs-status-filter-label"
                      label="Status"
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          status: e.target.value as string,
                        }))
                      }
                      sx={{ height: "56px", borderRadius: "8px" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="CANCELED">Canceled</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      width: {
                        xs: "100%",
                        md: "240px",
                      },
                    }}
                    size="small"
                  >
                    <InputLabel id="programs-org-filter-label">
                      Organization
                    </InputLabel>
                    <Select
                      labelId="programs-org-filter-label"
                      label="Organization"
                      value={filters.orgUnitId}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          orgUnitId: e.target.value as string,
                        }))
                      }
                      sx={{ height: "56px", borderRadius: "8px" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {organizationOptions.map((o) => (
                        <MenuItem key={o._id} value={o._id}>
                          {o.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={programsList?.totalCount}
                  isLoading={isLoadingProgramsList || isFetchingProgramsList}
                  onChangePage={(event, newPage) => {
                    setFilters((f) => ({ ...f, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((f) => ({
                      ...f,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((f) => ({
                      ...f,
                      pageNumber: 0,
                      sortField: orderBy as string,
                      sortOrder: order === "asc" ? 1 : -1,
                    }));
                  }}
                  messageEmptyData="No programs to show."
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <ProgramModal
          open={modalState.open}
          onClose={handleCloseModal}
          program={modalState.program}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          title="Delete"
          description="Are you sure you want to delete this registry?"
          theme={theme}
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveProgram.isPending}
        />
      )}
    </>
  );
};

export default ProgramsPage;
